<template>
    <v-dialog
        v-model="dialogModel"
        width="900"
        max-width="95%"
        @click:outside.prevent="closeDialog"
    >
        <v-card>
            <v-card-title class="text-h6 text-md-h5 grey lighten-2">
                <v-row>
                    <v-col cols="10">
                        P46: Employee without a Form P45
                    </v-col>
                    <v-col cols="2" class="text-right">
                        <v-btn icon @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text class="py-5" style="max-height: 75vh; overflow-y: auto;">
                <v-form ref="employeeWithoutP45Form" lazy-validation>
                    <p class="mb-2">Section one:</p>
                    <p class="mb-2">To be completed by the employee</p>
                    <p class="mb-2">
                        Please complete section one and then hand the form back to your present employer. If you later receive a form P45 from your previous employer, hand it to your present employer.
                        Use capital letters when completing this form.
                    </p>
                    <p class="mb-10">
                        <b>This is very important in getting your tax and benefits right</b>
                    </p>

                    <div class="mb-4" style="border-bottom:1px solid #e0e0e0">
                        <h2 class="mb-2">Your details</h2>
                    </div>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">National Insurance Number <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nationalInsuranceNumber"
                                placeholder="National Insurance Number"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="National Insurance Number"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Date of Birth <span class="red--text">*</span></h3>
                            <v-menu v-model="dobMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="form.dateOfBirthFormatted"
                                        placeholder="DD-MM-YYYY"
                                        readonly
                                        outlined
                                        dense
                                        prepend-icon="mdi-calendar"
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="[requiredRules]"
                                        hint="Date"
                                        :persistent-hint="true"
                                    />
                                </template>
                                <v-date-picker v-model="form.dateOfBirth" :max="today" @input="dobMenu = false" />
                            </v-menu>
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Title <span class="red--text">*</span></h3>
                            <v-select
                                v-model="form.title"
                                :items="titleItems"
                                placeholder="Please Select"
                                outlined
                                dense
                                attach
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Full Name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.name.firstName"
                                placeholder="First Name"
                                outlined
                                dense
                                :rules="firstNameRules"
                                hint="First Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-end">
                            <v-text-field
                                v-model="form.name.lastName"
                                placeholder="Last Name"
                                outlined
                                dense
                                :rules="lastNameRules"
                                hint="Last Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">House/Flat Number <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.address.houseOrFlatNumber"
                                placeholder="House/Flat Number"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Rest of Address <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.address.line1"
                                placeholder="Street Address"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Street Address"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-end">
                            <v-text-field
                                v-model="form.address.line2"
                                placeholder="Street Address Line 2"
                                outlined
                                dense
                                hint="Street Address Line 2"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.address.city"
                                placeholder="City"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="City"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.address.state"
                                placeholder="State / Province"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="State / Province"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.address.postalCode"
                                placeholder="Postal / Zip Code"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Postal / Zip Code"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Gender <span class="red--text">*</span></h3>
                            <v-radio-group v-model="form.gender" row :rules="[requiredRules]">
                                <v-radio label="Male" value="male" />
                                <v-radio label="Female" value="female" />
                            </v-radio-group>
                        </v-col>
                    </v-row>

                    <div class="mb-4 pt-5" style="border-bottom:1px solid #e0e0e0">
                        <h2 class="mb-2">Statements</h2>
                    </div>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Read all the following statements carefully and tick the one that applies to you</h3>
                            <v-radio-group v-model="form.statement">
                                <v-radio
                                    label="A - This is my first job since last 6 April and I have not been receiving taxable Jobseeker's Allowance or taxable Incapacity Benefit or a state or occupational pension"
                                    value="a"
                                />
                                <v-radio
                                    label="B - This is now my only job, but since last 6 April I have had another job, or have received taxable Jobseeker's Allowance or Incapacity Benefit. I do not receive a state or occupational pension."
                                    value="b"
                                />
                                <v-radio
                                    label="C - I have another job or receive a state occupational pension."
                                    value="c"
                                />
                            </v-radio-group>
                        </v-col>
                    </v-row>

                    <div class="mb-4 pt-5" style="border-bottom:1px solid #e0e0e0">
                        <h2 class="mb-2">Student Loans</h2>
                    </div>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <p class="body-2 mb-2">
                                If you left a course of Higher Education before last 6 April and received your first Student Loan instalment on or after 1 September 1998 and you have not fully repaid your Student Loan, tick box D.
                                (If you are required to repay your Student Loan through your bank or building society account do not tick box D.)
                            </p>
                            <v-checkbox
                                v-model="form.studentLoanD"
                                class="mt-0"
                                label="D"
                            />
                        </v-col>
                    </v-row>

                    <div class="mt-5 pt-5" style="border-top:1px solid #e0e0e0">
                        <div class="d-flex justify-end">
                            <v-btn color="primary" :loading="submitLoading" @click="onSubmit">
                                Submit
                            </v-btn>
                        </div>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import validationRules from '@/common/validationRules'
    import { mapActions } from 'vuex'

    const emptyForm = () => ({
        nationalInsuranceNumber: '',
        dateOfBirth: null,
        dateOfBirthFormatted: '',
        title: '',
        name: {
            firstName: '',
            lastName: ''
        },
        address: {
            houseOrFlatNumber: '',
            line1: '',
            line2: '',
            city: '',
            state: '',
            postalCode: ''
        },
        gender: '',
        statement: '',
        studentLoanD: false
    })

    export default {
        name: 'EmployeeWithoutAccountFormDialog',
        props: ['isOpen', 'programme'],
        data: () => ({
            form: emptyForm(),
            submitLoading: false,
            dobMenu: false,
            requiredRules: validationRules.required,
            firstNameRules: validationRules.firstName,
            lastNameRules: validationRules.lastName,
            titleItems: ['Mr', 'Miss', 'Mrs', 'Ms', 'Dr']
        }),
        computed: {
            dialogModel: {
                get () {
                    return this.isOpen
                },
                set (value) {
                    this.$emit('update:isOpen', value)
                }
            },
            today () {
                return new Date().toISOString().slice(0, 10)
            }
        },
        watch: {
            'form.dateOfBirth' (val) {
                this.form.dateOfBirthFormatted = val ? Date.formatDate([val]).trim() : ''
            }
        },
        methods: {
            ...mapActions([
                'uploadP46EmployeeWithoutP45AccountsForm', 'showAlert'
            ]),
            getTitleOption (title) {
                const map = {
                    Mr: 1,
                    Miss: 2,
                    Mrs: 3,
                    Ms: 4,
                    Dr: 5
                }
                return map[title] || 0
            },
            getGenderOption (gender) {
                const map = {
                    male: 1,
                    female: 2
                }
                return map[gender] || 0
            },
            getStatementOption (statement) {
                const map = {
                    a: 1,
                    b: 2,
                    c: 3
                }
                return map[statement] || 0
            },
            buildPayload () {
                return {
                    transitionProgrammeCourseId: this.programme && this.programme.id ? this.programme.id : 0,
                    nationalInsuranceNumber: this.form.nationalInsuranceNumber,
                    dateOfBirth: this.form.dateOfBirth,
                    title: this.getTitleOption(this.form.title),
                    firstName: this.form.name.firstName,
                    lastName: this.form.name.lastName,
                    houseFlatNumber: this.form.address.houseOrFlatNumber,
                    streetAddress: this.form.address.line1,
                    streetAddressLine2: this.form.address.line2,
                    city: this.form.address.city,
                    stateProvince: this.form.address.state,
                    postalZipCode: this.form.address.postalCode,
                    gender: this.getGenderOption(this.form.gender),
                    statement: this.getStatementOption(this.form.statement),
                    studentLoanD: this.form.studentLoanD,
                    formType: 'P46EmployeeWithoutP45Accounts'
                }
            },
            resetForm () {
                this.form = emptyForm()
            },
            closeDialog () {
                this.resetForm()
                this.$emit('update:isOpen', false)
            },
            async onSubmit () {
                const formRef = this.$refs.employeeWithoutP45Form
                if (!formRef.validate()) {
                    this.showAlert({
                        text: 'Fill all the required fields please',
                        type: 'error'
                    })
                    return
                }

                this.submitLoading = true
                try {
                    const payload = this.buildPayload()
                    await this.uploadP46EmployeeWithoutP45AccountsForm(payload)
                    this.$emit('saved')
                    this.closeDialog()
                    if (formRef && formRef.resetValidation) formRef.resetValidation()
                } catch (e) {
                    this.showAlert({
                        text: e.message || 'Error saving form',
                        type: 'error'
                    })
                } finally {
                    this.submitLoading = false
                }
            }
        }
    }
</script>
